/**
 * widgetSurfacePreferences.ts — [T246.5-FE-HOOKS]
 *
 * Type contracts + deterministic key serializer for the
 * `/api/v1/users/me/preferences/widgets` surfaces. The serializer is the
 * single source of truth that every TanStack Query hook in
 * `features/dashboard/widgetSurfacePreferencesApi.ts` uses to derive a
 * stable `queryKey` prefix, so surfaces with the same semantic identity
 * always share a cache entry — regardless of property declaration order.
 *
 * Public surface (the contract for AC1):
 *   • Types
 *       - `WidgetSurfacePreferencesEntry`  — the server's per-surface entry
 *                                            (`enabled`, `available`, `default`,
 *                                            `updatedAt`).
 *       - `WidgetSurfacePreferences`       — full surface map keyed by
 *                                            surface name.
 *       - `ToggleWidgetInput`              — input to the `/widgets/toggle`
 *                                            mutation (`kind` + `enabled`).
 *       - `UseGetWidgetSurfacePreferencesArgs`
 *                                            — argument shape for the GET hook
 *                                              (`surface?: string`).
 *
 *   • Constants
 *       - `WIDGET_SURFACE_PREF_KEY_PREFIX` — queryKey namespace
 *                                            (default: `'widget-surface-prefs'`).
 *       - `WIDGET_SURFACE_PREF_STALE_TIME_MS`
 *                                            — 5-minute staleTime, exported so
 *                                              the page (Wave 6) can reuse the
 *                                              same value if it builds its own
 *                                              revalidation timer.
 *
 *   • Helpers
 *       - `serializeWidgetSurfaceKey(args)` — produces the tuple
 *                                              `[prefix, surface ?? 'all']` that
 *                                              the GET hook uses as its
 *                                              `queryKey`. `'all'` is the
 *                                              canonical marker for the
 *                                              all-surfaces query (no surface
 *                                              arg).
 *       - `stableStringify(value)`         — JSON.stringify with sorted keys.
 *                                              Mirrors the existing
 *                                              `useInsightsCache.stableStringify`
 *                                              (T246-FE-CACHE) so the codebase
 *                                              has exactly two stringifiers
 *                                              total: this one (lightweight,
 *                                              allocations-free for keys) and
 *                                              the hooked one (SHA-1 wrapped).
 *
 * React Query docs: https://tanstack.com/query/latest
 *
 * @module lib/widgetSurfacePreferences
 * @see .omo/plans/T246-PY-insights-graph-revamp-prd.md §13.4 (FE-HOOKS)
 *      + §13.5 (R1) optimistic rollback race mitigation
 */

// ── Public types ───────────────────────────────────────────────────────────

/**
 * Per-surface entry as returned by
 * `GET /api/v1/users/me/preferences/widgets?surface=X`.
 *
 * Mirrors the backend `buildSurfacePayload()` return shape in
 * `backend/src/handlers/preferencesHandler.ts:155-164`. The `available` and
 * `default` lists are always returned (even when the user has not curated
 * the surface yet) so the FE can render an empty-vs-populated state
 * without an extra round-trip.
 */
export interface WidgetSurfacePreferenceEntry {
  surface: string;
  enabled: string[];
  available: string[];
  default: string[];
  updatedAt: string | null;
}

/**
 * Full surface map returned by
 * `GET /api/v1/users/me/preferences/widgets` (no surface query param).
 *
 * Shape: `{ [surface: string]: WidgetSurfacePreferenceEntry }`. The handler
 * also returns a top-level `migrated: boolean` flag — we intentionally
 * omit that because the FE consumer only cares about the per-surface data
 * (migration is a backend observability concern).
 */
export type WidgetSurfacePreferences = Record<
  string,
  WidgetSurfacePreferenceEntry
>;

/**
 * Input for the `/widgets/toggle` mutation. The handler derives the surface
 * server-side from the user's already-curated surfaces (today only
 * `transactions`).
 *
 * Shape matches
 * `widgetSurfaceToggleSchema` in `backend/src/validators/preferencesValidator.ts:95`.
 */
export interface ToggleWidgetInput {
  kind: string;
  enabled: boolean;
}

/**
 * Argument shape for `useGetWidgetSurfacePreferences(surface?)`.
 *
 * Passing `undefined` (or omitting the surface) targets the all-surfaces GET.
 * The `'all'` literal is reserved as the canonical cache-key marker for that
 * branch — it is treated as a falsy "no specific surface" sentinel inside the
 * serializer, never as a real surface name.
 */
export interface UseGetWidgetSurfacePreferencesArgs {
  surface?: string;
}

// ── Constants ─────────────────────────────────────────────────────────────

/**
 * Cache-key namespace for every widget-surface-preferences query. The
 * serializer prepends it so the GET hook's `queryKey` is always at least
 * two long (`[prefix, 'all' | <surface>]`). Centralised so consumers (the
 * Wave 6 page, the Wave 8 cross-surface regression test) don't drift to
 * ad-hoc prefixes.
 */
export const WIDGET_SURFACE_PREF_KEY_PREFIX = 'widget-surface-prefs' as const;

/**
 * Marker used as the `queryKey[1]` slot when the GET hook is invoked with
 * no surface arg (the all-surfaces request). Distinct from the empty
 * string so the cache key is human-readable in the devtools.
 */
export const WIDGET_SURFACE_PREF_ALL = 'all' as const;

/**
 * 5-minute staleTime for the GET hook.
 *
 * Why 5 minutes: toggling tabs back and forth (e.g. user goes
 * /widgets/insights → /transactions → /widgets/insights) should not
 * trigger a refetch storm, but a stale read older than 5 minutes is
 * unlikely to reflect fresh user intent on the curation UI.
 *
 * The mutation hooks (`useToggle*`, `useReplace*`, `useReset*`) always
 * invalidate the GET on settled, so the staleTime clock effectively
 * restarts after every write — this 5 minutes is only the cold-cache floor.
 */
export const WIDGET_SURFACE_PREF_STALE_TIME_MS = 5 * 60 * 1000;

// ── Helpers ────────────────────────────────────────────────────────────────

/**
 * Deterministic JSON serialisation with keys sorted lexicographically.
 *
 * Why: `JSON.stringify({ a: 1, b: 2 })` and `JSON.stringify({ b: 2, a: 1 })`
 * produce byte-different strings, which would hash to different cache
 * keys and break equality. Sorting the keys guarantees same semantic set
 * → same string, regardless of declaration order.
 *
 * Mirrors the lightweight subset of `useInsightsCache.stableStringify`
 * (T246-FE-CACHE) — kept as a SECOND copy here on purpose: this one is
 * dependency-free (no SHA-1, no WeakSet, no Date.toISOString side effects)
 * so it can be used as a React-Query `queryKeyHashFn` without exploding
 * the bundle or pulling in the full Insights cache machinery.
 *
 * Behaviour notes:
 *   - `undefined` is preserved (key left absent in objects, emitted as
 *     the JSON spec literal `"__undefined__"` for safety in arrays).
 *   - `Date` serialises via `toISOString()` so date ordering is
 *     deterministic across browser sessions.
 *   - Circular references are replaced with the literal `"[Circular]"` so
 *     the call never throws — matches the cache hook's safety behaviour.
 */
export function stableStringify(value: unknown): string {
  const seen = new WeakSet<object>();
  const visit = (node: unknown): string => {
    if (node === null) return 'null';
    const nodeType = typeof node;
    if (nodeType === 'undefined') return '"__undefined__"';
    if (nodeType === 'string') return JSON.stringify(node);
    if (nodeType === 'number') {
      return Number.isFinite(node as number) ? String(node) : 'null';
    }
    if (nodeType === 'boolean') return String(node);
    if (nodeType === 'bigint') return `"${(node as bigint).toString()}n"`;
    if (nodeType === 'function') return '"__function__"';

    if (node instanceof Date) return JSON.stringify(node.toISOString());

    if (Array.isArray(node)) {
      if (seen.has(node)) return '"[Circular]"';
      seen.add(node);
      const parts = node.map((entry) => visit(entry));
      seen.delete(node);
      return `[${parts.join(',')}]`;
    }

    if (nodeType === 'object') {
      const obj = node as Record<string, unknown>;
      if (seen.has(obj)) return '"[Circular]"';
      seen.add(obj);
      const keys = Object.keys(obj).sort();
      const parts: string[] = [];
      for (const key of keys) {
        parts.push(`${JSON.stringify(key)}:${visit(obj[key])}`);
      }
      seen.delete(obj);
      return `{${parts.join(',')}}`;
    }

    return '"__unknown__"';
  };
  return visit(value);
}

/**
 * Produce the stable `queryKey` tuple for the GET hook.
 *
 * Contract:
 *   - `serializeWidgetSurfaceKey(undefined)`
 *       → `['widget-surface-prefs', 'all']`
 *   - `serializeWidgetSurfaceKey('transactions')`
 *       → `['widget-surface-prefs', 'transactions']`
 *   - `serializeWidgetSurfaceKey('')` is normalised to `'all'` so empty
 *     strings (e.g. uninitialised form state) never produce a phantom
 *     per-empty-string cache entry.
 *   - Property declaration order of the args object does not matter —
 *     even though the current arg shape only has a single `surface`
 *     field, the serializer runs through `stableStringify` so a future
 *     multi-field arg cannot regress cache equality.
 *
 * The function is pure (no closures over mutable state) so the test
 * suite can call it deterministically.
 */
export function serializeWidgetSurfaceKey(
  args?: UseGetWidgetSurfacePreferencesArgs,
): [typeof WIDGET_SURFACE_PREF_KEY_PREFIX, string] {
  if (!args) {
    return [WIDGET_SURFACE_PREF_KEY_PREFIX, WIDGET_SURFACE_PREF_ALL];
  }
  const normalised = args.surface && args.surface.length > 0
    ? args.surface
    : WIDGET_SURFACE_PREF_ALL;
  return [WIDGET_SURFACE_PREF_KEY_PREFIX, normalised];
}

/**
 * Stable-stringify the args object so two equivalent arg shapes (same set
 * of fields, different declaration order) produce the same canonical
 * string. Useful for the Wave-6 page that may want to derive a React Query
 * `queryKeyHashFn` consistent with our serializer.
 */
export function serializeWidgetSurfaceKeyArgs(
  args?: UseGetWidgetSurfacePreferencesArgs,
): string {
  return stableStringify(args ?? {});
}
